import React from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { X, Filter, Tv, Clapperboard } from "lucide-react";
import { useLocale } from "@/lib/locale";

export default function FilterBar({
  platforms, countries, languages,
  filters, setFilters, advanced = true,
}) {
  const { t } = useLocale();
  const clear = () => setFilters({
    platform: "", country: "", language: "",
    kind: "", year_min: "", year_max: "", sort: "popularity",
  });
  const active = filters.platform || filters.country || filters.language ||
                 filters.kind || filters.year_min || filters.year_max ||
                 (filters.sort && filters.sort !== "popularity");

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 60 }, (_, i) => currentYear - i);

  return (
    <div className="glass rounded-md p-4 flex flex-wrap items-center gap-3" data-testid="filter-bar">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-500 font-heading pr-2 border-r border-white/10">
        <Filter className="h-3.5 w-3.5" /> {t("refine")}
      </div>

      {advanced && (
        <div className="inline-flex rounded-md border border-white/10 overflow-hidden" data-testid="kind-toggle">
          <button
            onClick={() => setFilters({ ...filters, kind: filters.kind === "movie" ? "" : "movie" })}
            className={`px-3 py-1.5 text-xs font-heading inline-flex items-center gap-1.5 transition ${
              filters.kind === "movie" ? "bg-amber text-obsidian" : "text-zinc-300 hover:bg-white/5"
            }`}
            data-testid="kind-toggle-movie"
          >
            <Clapperboard className="h-3.5 w-3.5" /> {t("movies_only")}
          </button>
          <button
            onClick={() => setFilters({ ...filters, kind: filters.kind === "tv" ? "" : "tv" })}
            className={`px-3 py-1.5 text-xs font-heading inline-flex items-center gap-1.5 transition border-l border-white/10 ${
              filters.kind === "tv" ? "bg-amber text-obsidian" : "text-zinc-300 hover:bg-white/5"
            }`}
            data-testid="kind-toggle-tv"
          >
            <Tv className="h-3.5 w-3.5" /> {t("tv_only")}
          </button>
        </div>
      )}

      <Select value={filters.platform || "all"} onValueChange={(v) => setFilters({ ...filters, platform: v === "all" ? "" : v })}>
        <SelectTrigger className="w-[150px] bg-black/30 border-white/10 text-zinc-200 font-heading" data-testid="filter-platform">
          <SelectValue placeholder={t("platform")} />
        </SelectTrigger>
        <SelectContent className="bg-obsidian-surface border-white/10 text-zinc-200">
          <SelectItem value="all">{t("all_platforms")}</SelectItem>
          {platforms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filters.country || "all"} onValueChange={(v) => setFilters({ ...filters, country: v === "all" ? "" : v })}>
        <SelectTrigger className="w-[150px] bg-black/30 border-white/10 text-zinc-200 font-heading" data-testid="filter-country">
          <SelectValue placeholder={t("country")} />
        </SelectTrigger>
        <SelectContent className="bg-obsidian-surface border-white/10 text-zinc-200 max-h-80">
          <SelectItem value="all">{t("all_countries")}</SelectItem>
          {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={filters.language || "all"} onValueChange={(v) => setFilters({ ...filters, language: v === "all" ? "" : v })}>
        <SelectTrigger className="w-[150px] bg-black/30 border-white/10 text-zinc-200 font-heading" data-testid="filter-language">
          <SelectValue placeholder={t("language")} />
        </SelectTrigger>
        <SelectContent className="bg-obsidian-surface border-white/10 text-zinc-200">
          <SelectItem value="all">{t("all_languages")}</SelectItem>
          {languages.map((l) => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {advanced && (
        <>
          <div className="inline-flex items-center gap-1.5">
            <Select
              value={filters.year_min || "any"}
              onValueChange={(v) => setFilters({ ...filters, year_min: v === "any" ? "" : v })}
            >
              <SelectTrigger className="w-[110px] bg-black/30 border-white/10 text-zinc-200 font-heading" data-testid="filter-year-min">
                <SelectValue placeholder={`${t("year_range")} ≥`} />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-surface border-white/10 text-zinc-200 max-h-72">
                <SelectItem value="any">Any</SelectItem>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-zinc-500">–</span>
            <Select
              value={filters.year_max || "any"}
              onValueChange={(v) => setFilters({ ...filters, year_max: v === "any" ? "" : v })}
            >
              <SelectTrigger className="w-[110px] bg-black/30 border-white/10 text-zinc-200 font-heading" data-testid="filter-year-max">
                <SelectValue placeholder={`${t("year_range")} ≤`} />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-surface border-white/10 text-zinc-200 max-h-72">
                <SelectItem value="any">Any</SelectItem>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Select
            value={filters.sort || "popularity"}
            onValueChange={(v) => setFilters({ ...filters, sort: v })}
          >
            <SelectTrigger className="w-[140px] bg-black/30 border-white/10 text-zinc-200 font-heading" data-testid="filter-sort">
              <SelectValue placeholder={t("sort_by")} />
            </SelectTrigger>
            <SelectContent className="bg-obsidian-surface border-white/10 text-zinc-200">
              <SelectItem value="popularity">{t("sort_popularity")}</SelectItem>
              <SelectItem value="year">{t("sort_year")}</SelectItem>
            </SelectContent>
          </Select>
        </>
      )}

      {active && (
        <button
          onClick={clear}
          className="ml-auto inline-flex items-center gap-1 text-xs text-amber hover:text-amber-hover font-heading"
          data-testid="filter-clear"
        >
          <X className="h-3 w-3" /> {t("clear")}
        </button>
      )}
    </div>
  );
}
